import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import useFetch from '../../api/useFetch';
import { tokens } from '../../global/theme/tokens';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ExoDialog from '../exo/ExoDialog';
import ExoForm from '../exo/ExoForm';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import * as yup from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateOrCreate } from '../special/updateOrCreate';
import { hasPermissions } from '../../auth/hasPermissions';
import RenderHTML from '../preview/RenderHTML';
import PublicIcon from '@mui/icons-material/Public';

export const ECTextBlocks = ({
  onSelect,
  type,
  className = 'flex flex-col gap-2',
  useHtml = true,
}) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [open, setOpen] = useState(null);

  const fields = [
    { label: t('Title'), key: 'title', type: 'text' },
    {
      label: t('Public'),
      key: 'isPublic',
      type: 'checkbox',
      hidden: !hasPermissions('text-block', 'write'),
    },
    {
      label: t('Text'),
      key: 'text',
      type: useHtml ? 'richTextEditor' : 'text',
    },
  ];

  const validationSchema = yup.object().shape({
    title: yup.string().required('Title is required'),
    text: yup.string().required('Text is required'),
  });

  const { apiData, setApiData } = useFetch(
    `text-blocks?type[eq]=${type || 'block'}&sortBy=title&sortOrder=asc`
  );

  function handleSelect(block) {
    DBRequest({
      config,
      path: `text-blocks/${block.id}/increment-use-count`,
      method: API_POST({}),
    });
    onSelect(block);
  }

  function handleSubmit(block) {
    let data = block;
    data = {
      ...data,
      isPublic:
        typeof data.isPublic === 'boolean'
          ? data.isPublic
          : data.isPublic === '1',
    };

    DBRequest({
      config,
      path: data.id ? `text-blocks/${data.id}` : `text-blocks`,
      method: block.id ? API_PATCH(data) : API_POST({ ...data, type: type }),
      onResponse: handleResponse,
    });
  }

  function handleResponse(res) {
    setApiData({ data: updateOrCreate(apiData.data, res.data) });
    setOpen(null);
  }

  function handleDelete(block) {
    DBRequest({
      config,
      path: `text-blocks/${block.id}`,
      method: API_DELETE(),
      onResponse: () => handleDeleteResponse(block),
    });
  }

  function handleDeleteResponse(block) {
    const newApiData = apiData.data.filter((el) => el.id != block.id);
    setApiData({ data: newApiData });
    setOpen(null);
  }

  return (
    <Box
      className={className}
      sx={{
        '.add-button': {
          opacity: 0,
          transition: '0.3s ease',
        },
        ':hover': {
          '.add-button': {
            opacity: 1,
          },
        },
      }}
    >
      <Box className="flex justify-between items-center">
        <Box className="flex gap-2 h-[38px] items-center">
          <TextSnippetIcon />
          <Typography>{t('Text blocks')}</Typography>
        </Box>

        <IconButton className="add-button" onClick={() => setOpen({})}>
          <AddIcon />
        </IconButton>
      </Box>
      {apiData &&
        apiData.data &&
        apiData.data.map((block) => (
          <TextBlock
            key={block.id}
            block={block}
            onSelect={handleSelect}
            onEdit={
              (block.isPublic && hasPermissions('text-block', 'write')) ||
              !block.isPublic
                ? (block) => setOpen(block)
                : null
            }
          />
        ))}
      <ExoDialog open={open} limitWidth>
        {open && (
          <>
            {open.id && (
              <Box className="absolute top-3 right-3" sx={{ zIndex: 10 }}>
                <Tooltip title={t('Delete text block')}>
                  <IconButton onClick={() => handleDelete(open)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <ExoForm
              header={
                Object.keys(open).length == 0
                  ? t('Create new text block')
                  : t('Edit text block')
              }
              onCancel={() => {
                setOpen(null);
              }}
              fields={fields}
              onSubmit={handleSubmit}
              startDataCollection={open}
              validationSchema={validationSchema}
            />
          </>
        )}
      </ExoDialog>
    </Box>
  );
};

const TextBlock = ({ block, onSelect, onEdit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box
      className="rounded-xl flex justify-between gap-4 "
      sx={{ backgroundColor: colors.bgInput }}
    >
      <Box
        className="p-2 pl-3 w-full "
        onDoubleClick={onEdit ? () => onEdit(block) : null}
        sx={{
          ':hover': {
            cursor: onEdit ? 'pointer' : 'default',
          },
        }}
      >
        <Box className="flex gap-2 items-center">
          <Typography variant="h4">{block.title}</Typography>
          {block.isPublic ? (
            <Tooltip title={t('Public')}>
              <PublicIcon />
            </Tooltip>
          ) : (
            <></>
          )}
        </Box>
        <RenderHTML
          HTML={block.text}
          className="w-[80%] opacity-70 max-h-[150px] pt-2 overflow-y-auto"
          sx={{ overflow: 'hidden' }}
        />
      </Box>
      <Button
        onClick={() => onSelect(block)}
        className="h-full flex justify-center items-center w-[60px] min-w-[60px] rounded-r-lg"
        sx={{
          borderRadius: '0px 8px 8px 0px',
          ':hover': {
            cursor: 'pointer',
            backgroundColor: colors.selected,
          },
        }}
      >
        <AddIcon fontSize="large" className="opacity-70" />
      </Button>
    </Box>
  );
};
